'use client'

import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

// Form validation schema
const formSchema = z.object({
  displayName: z.string().min(2, 'Workspace name must be at least 2 characters')
})

type FormValues = z.infer<typeof formSchema>

interface IWorkspaceDetailsFormProps {
  initialValues: {
    displayName: string
  }
  isLoading: boolean
  onCancelAction: () => void
  onSubmitAction: (displayName: string) => Promise<void>
}

/**
 * Form component for editing workspace details
 */
export const WorkspaceDetailsForm = ({
  initialValues,
  onSubmitAction,
  onCancelAction,
  isLoading
}: IWorkspaceDetailsFormProps) => {
  // Form setup with initial values
  const form = useForm<FormValues>({
    defaultValues: {
      displayName: initialValues.displayName
    },
    resolver: zodResolver(formSchema)
  })

  /**
   * Handle form submission
   */
  const handleSubmit = async (data: FormValues) => {
    await onSubmitAction(data.displayName)
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit workspace details</DialogTitle>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-4">
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Workspace Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter workspace name" {...field} className="max-w-full text-base" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancelAction} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  )
}
